import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { getLscArchiveAutopublishSafety, getLscArchiveAutopublishSettings } from '../../api';
import { archiveAutopublishCommandCenterSummary } from '../../commandCenterCopy';
import {
  ARCHIVE_RETENTION_COMMAND_CENTER_COPY,
  ARCHIVE_RETENTION_SUMMARY_COPY,
} from '../../archiveRetentionCopy';
import { lscPath } from '../../routing';
import type { LscArchiveAutopublishKillSwitch, LscArchiveAutopublishSettings } from '../../types';

export type ArchiveAutopublishCommandCenterSummaryProps = {
  leagueId: string;
};

const ArchiveAutopublishCommandCenterSummary: React.FC<ArchiveAutopublishCommandCenterSummaryProps> = ({
  leagueId,
}) => {
  const [settings, setSettings] = useState<LscArchiveAutopublishSettings | null>(null);
  const [safety, setSafety] = useState<LscArchiveAutopublishKillSwitch | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settingsPayload, safetyPayload] = await Promise.all([
          getLscArchiveAutopublishSettings(leagueId),
          getLscArchiveAutopublishSafety(leagueId),
        ]);
        if (!cancelled) {
          setSettings(settingsPayload);
          setSafety(safetyPayload);
        }
      } catch {
        if (!cancelled) {
          setSettings(null);
          setSafety(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const summary = archiveAutopublishCommandCenterSummary(settings?.autopublish_mode, safety);

  return (
    <div
      className="rounded-lg border border-secondary-700 bg-secondary-900/40 p-4 space-y-3 min-w-0 overflow-x-hidden"
      data-testid="archive-autopublish-command-center-summary"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h3 className="text-sm font-semibold text-white">Archive auto-publish (Stage 2)</h3>
        <Link
          to={lscPath(`/leagues/${leagueId}/settings/archive-automation`)}
          className="text-xs text-primary-300 hover:text-primary-200 inline-flex min-h-[44px] items-center"
        >
          Open settings
        </Link>
      </div>
      <p className="text-sm text-secondary-300">{summary}</p>
      <p className="text-xs text-secondary-500">No archive will publish automatically yet.</p>
      <p className="text-xs text-secondary-500">{ARCHIVE_RETENTION_COMMAND_CENTER_COPY}</p>
      <p className="text-xs text-secondary-500">{ARCHIVE_RETENTION_SUMMARY_COPY}</p>
    </div>
  );
};

export default ArchiveAutopublishCommandCenterSummary;
